const NOME_DO_TIPO = {
    multipla: "`multipla` — múltipla escolha, de duas a cinco alternativas",
    certo_errado: "`certo_errado` — afirmação para julgar como certa ou errada",
    cartao: "`cartao` — frente e verso, sem alternativas",
    cloze: "`cloze` — lacunas para completar",
};
/**
 * O bloco que descreve o formato da resposta.
 *
 * `faixa` é opcional porque os dois pedidos chegam aqui em situações
 * diferentes: quem pede a partir dos erros (RN-46) não sabe quantos itens vão
 * voltar, então não tem faixa para citar; quem pede um lote novo escolheu a
 * quantidade e sabe exatamente onde ela começa e termina.
 */
export function blocoDoFormato(entrada) {
    const ids = entrada.faixa
        ? `\`id\`: inteiro, **de ${entrada.faixa.inicio} a ${entrada.faixa.fim}**, sem repetir. ` +
            "Essa faixa está livre nesta trilha — usar outra obriga a deslocar o lote inteiro na importação."
        : "`id`: inteiro positivo, único dentro do arquivo, começando em um número que eu vou ajustar.";
    return [
        "## Formato da resposta",
        "",
        "**Devolva apenas um bloco de JSON**, sem texto antes nem depois, exatamente nesta forma:",
        "",
        "```json",
        "{",
        '  "versao": 1,',
        `  "trilha": ${JSON.stringify(entrada.trilha)},`,
        `  "lote": ${JSON.stringify(entrada.lote)},`,
        '  "fonte": "gerado por modelo de linguagem",',
        '  "itens": [',
        "    {",
        `      "id": ${entrada.faixa?.inicio ?? 1},`,
        '      "categoria": ["Matéria", "Assunto"],',
        '      "tipo": "multipla",',
        '      "enunciado": "O texto da pergunta.",',
        '      "conteudo": { "alternativas": { "A": "primeira", "B": "segunda", "C": "terceira" } },',
        '      "gabarito": { "correta": "B" },',
        '      "explicacao": "Por que a B está certa e por que as outras não estão.",',
        '      "dificuldade": "média"',
        "    }",
        "  ]",
        "}",
        "```",
        "",
        "Campo a campo:",
        "",
        `- ${ids}`,
        "- `categoria`: array de textos, o caminho na árvore, **exatamente como escrito acima**. Não invente níveis novos nem reescreva os nomes.",
        "- `tipo` decide o par `conteudo`/`gabarito`, e eles não se misturam:",
        '  - `multipla`: `conteudo` é `{ "alternativas": { "A": "…", "B": "…" } }`, de duas a cinco chaves entre A e E; `gabarito` é `{ "correta": "B" }`, e a letra precisa existir nas alternativas.',
        '  - `certo_errado`: `conteudo` é `{}`; `gabarito` é `{ "correta": true }` ou `false`.',
        '  - `cartao`: `conteudo` é `{ "verso": "o que aparece ao virar" }`; `gabarito` é `{}`.',
        '  - `cloze`: `conteudo` é `{ "lacunas": [{ "id": "l1", "resposta": "texto", "aceitaveis": ["variante"] }] }`; `gabarito` é `{}`.',
        "- `enunciado`: o texto da pergunta, não vazio.",
        "- `explicacao`: **obrigatória**, e precisa dizer *por que* a resposta certa é certa — não repetir o enunciado.",
        "- `dificuldade`: texto livre e opcional.",
        "",
        "Acentuação correta, em português. Nada de campos fora desta lista: campo desconhecido faz o arquivo ser recusado.",
    ].join("\n");
}
/**
 * O pedido inteiro: o que gerar, sobre o quê, onde pendurar e como responder.
 */
export function montarPedidoDeGeracao(p) {
    const partes = [];
    const assunto = p.assunto.trim();
    const faixa = { inicio: p.primeiroId, fim: p.primeiroId + Math.max(p.quantidade, 1) - 1 };
    partes.push(`# ${p.quantidade} questões novas para a trilha ${p.trilha}`);
    partes.push("");
    partes.push(assunto.length > 0
        ? `Gere questões de estudo sobre o seguinte:\n\n> ${assunto.replace(/\n+/g, "\n> ")}`
        : "Gere questões de estudo sobre o assunto da categoria indicada abaixo.");
    partes.push("");
    if (p.categoria.length > 0) {
        partes.push(`Todos os itens vão para a categoria **${p.categoria.join(" › ")}**, e o campo ` +
            `\`categoria\` de cada um é exatamente \`${JSON.stringify(p.categoria)}\`.`);
    }
    else {
        /*
         * Sem categoria escolhida, o pedido não pode ficar em silêncio: o campo é
         * obrigatório no formato, e um modelo sem instrução inventa o caminho. Pedir
         * que ele derive da descrição é a única saída honesta — e a frase diz que a
         * árvore nasce daí, para que ele escolha um nome que se possa conviver.
         */
        partes.push("Escolha você o caminho de categoria, derivado da descrição acima, com um ou dois níveis " +
            "(`[\"Matéria\"]` ou `[\"Matéria\", \"Assunto\"]`), e **use o mesmo caminho em todos os itens**. " +
            "Ele vai virar pasta na minha árvore de estudo, então prefira o nome que um índice de livro usaria.");
    }
    partes.push("");
    if (p.tipos.length > 0) {
        partes.push("Tipos de item a usar:");
        partes.push("");
        for (const t of p.tipos)
            partes.push(`- ${NOME_DO_TIPO[t]}`);
    }
    else {
        partes.push("Use os tipos que couberem no assunto: `multipla`, `certo_errado`, `cartao` ou `cloze`.");
    }
    partes.push("");
    if (p.dificuldade) {
        partes.push(`Dificuldade pretendida: **${p.dificuldade}**.`);
        partes.push("");
    }
    partes.push("Cada questão precisa cobrar entendimento, não memória de frase: evite pergunta que se " +
        "responde reconhecendo a redação literal do enunciado. Nas de múltipla escolha, as " +
        "alternativas erradas precisam ser erros plausíveis — alternativa absurda entrega a resposta.");
    partes.push("");
    partes.push(`Escreva no nível da prova ou do objetivo da trilha **${p.trilha}**: cobre só o que ela cobra, ` +
        "sem ferramentas de nível acima (cálculo numa prova de ensino médio, por exemplo). " +
        "Se a descrição acima não for um assunto de estudo, não gere nada: devolva `\"itens\": []`.");
    partes.push("");
    partes.push("**Antes de responder, confira cada questão e corrija ou descarte a que falhar:** " +
        "(1) existe uma única resposta certa, e o gabarito aponta para ela; " +
        "(2) o enunciado não admite duas leituras; " +
        "(3) nenhuma alternativa errada pode ser defendida como certa; " +
        "(4) a explicação bate com o gabarito; " +
        "(5) os fatos e as contas estão corretos — refaça a conta antes de fixar a letra.");
    partes.push("");
    partes.push("---");
    partes.push("");
    partes.push(blocoDoFormato({ trilha: p.trilha, lote: p.lote, faixa }));
    partes.push("");
    return partes.join("\n");
}
/**
 * O nome sugerido do lote, a partir da descrição.
 *
 * Ele é sugestão editável, não imposição: o nome do lote é o que se digita para
 * excluí-lo em bloco depois (RN-25), e um identificador gerado que ninguém
 * reconhece seis meses depois torna essa exclusão perigosa. Sai em minúsculas,
 * sem acento e com hífen, que é a forma dos lotes que já existem.
 */
export function loteSugerido(assunto, hoje) {
    const base = assunto
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .split("-")
        .filter(Boolean)
        .slice(0, 4)
        .join("-");
    const dia = hoje.slice(0, 10);
    return base.length > 0 ? `${base}-${dia}` : `lote-${dia}`;
}
//# sourceMappingURL=pedido-de-itens.js.map